import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useAxios from '../../../hooks/useAxios';
import { RootState } from '../../../store/rootReducer';
import {
  getDetailTimetable2,
  updateTimetable,
} from '../../../api/campusClasses3.api';
import moment from 'moment';
import Button from '../../Button';
import { DatePicker, Select } from 'antd';
import Loader from '../../Spinner';
import ErrorResponse from '../../ErrorResponse';
import { getCampusClassrooms } from '../../../api/classrooms.api';
import { IStaff } from '../../../models/employees.model';
import SelectTeacherModal from '../../modals/SelectTeacherModal';
import useNotification from '../../../hooks/useNotification';
import _ from 'lodash';
import CustomTimePicker from '../../CustomTimePicker';
import useAuth from '../../../hooks/useAuth';
import { SEMESTER_TYPE } from '../../../constans';

const { Option } = Select;

interface IProps {
  setTab: (tab: string) => void;
  getTimetables: (classCode: string) => void;
}

const ClassCalendarDetail: FC<IProps> = ({ setTab, getTimetables }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { infoNotification, successNotification } = useNotification();
  const { data: timetable, isLoading, error, execute } = useAxios<any>();
  const {
    data: classrooms,
    isLoading: classroomLoading,
    error: classroomError,
    execute: classroomExecute,
  } = useAxios<any>();
  const {
    data: updateData,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios<any>();
  const { timetables } = useSelector(
    (state: RootState) => state.classTimetable,
  );

  const [timetableCode, setTimetableCode] = useState<string | null>(null);
  const [eventIndex, setEventIndex] = useState<number>(-1);
  const [data, setData] = useState<any>();
  const [inputs, setInputs] = useState<any>();
  const [classroomList, setClassroomList] = useState<any>([]);
  const [timetableData, setTimetableData] = useState<any>([]);

  // 강사 배정 모달 표시여부
  const [selectTeacherVisible, setSelectTeacherVisible] = useState(false);
  // 강사 모달을 호출한 input 정보
  const [teacherInputRef, setTeacherInputRef] = useState<any>();

  useEffect(() => {
    setTimetableCode(sessionStorage.getItem('timetableCode'));
    setEventIndex(Number(sessionStorage.getItem('eventIndex')));
    sessionStorage.removeItem('timetableCode');
    sessionStorage.removeItem('eventIndex');
  }, []);

  // 일정 상세를 가져온다
  useEffect(() => {
    if (timetableCode) {
      execute(getDetailTimetable2(timetableCode));
    }
  }, [timetableCode]);

  // 일정 상세 get 성공 시 처리
  useEffect(() => {
    if (timetable && !isLoading) {
      setData(timetable.data);
      setInputs({
        changeDate: timetable.data?.srtDate,
        classTime: timetable.data?.classTime.map((item: any) => {
          return {
            classTimeSeq: item.classTimeSeq,
            srtTime: item.srtTime,
            classRoomCode: item.classRoomCode,
            subjectName: item.subjectName,
            lessonName: item.lessonName,
            teacher: {
              memberCode: item.teacherCode,
              memberName: item.teacherKorName,
            },
          };
        }),
      });
    }
  }, [timetable, isLoading]);

  // 강의실 목록을 가져온다
  useEffect(() => {
    if (chosenClass) {
      classroomExecute(getCampusClassrooms(chosenClass.campusCode));
    }
  }, [chosenClass]);

  // 강의실 목록 get 성공 시 처리
  useEffect(() => {
    if (classrooms && !classroomLoading) {
      const _classrooms: any = [];
      classrooms.data[0]?.classroom.forEach((item: any) =>
        _classrooms.push({
          label: item.classroomName,
          value: item.classroomCode,
        }),
      );
      setClassroomList(_classrooms);
    }
  }, [classrooms, classroomLoading]);

  // 시간표 데이터 가공 (이전/이후 수업 참조용)
  useEffect(() => {
    if (timetables) {
      const _timetables = _.cloneDeep(timetables).sort((a: any, b: any) =>
        a?.srtDate < b?.srtDate ? -1 : a?.srtDate > b?.srtDate ? 1 : 0,
      );
      const data: any = [];
      _timetables.forEach((item, index) => {
        const year = Number(item?.srtDate.slice(0, 4));
        const month = Number(item?.srtDate.slice(4, 6)) - 1;
        const day = Number(item?.srtDate.slice(6));
        data.push({
          id: item.timetableCode,
          title: `${item.srtTime} ${item.classRoomName}`,
          allDay: true,
          start: new Date(year, month, day),
          end: new Date(year, month, day + 1),
          resource: {
            type: 'timetable',
            index,
          },
        });
      });

      setTimetableData(_timetables);
    }
  }, [timetables]);

  // 시간표 상세내역 update 성공 시 처리
  useEffect(() => {
    if (updateData && !updateIsLoading) {
      if (chosenClass) getTimetables(chosenClass.classCode);
      successNotification(t('common.message.edit1'));
      setTab('schedule');
    }
  }, [updateData, updateIsLoading]);

  // inputs 업데이트
  const onSetInputs = (key: any, value: any) => {
    if (key === 'changeDate') {
      // 월차를 넘어가지 않는지 확인
      if (
        moment(value).isBefore(timetable.data.monthSrtDate) ||
        moment(value).isAfter(timetable.data.monthEndDate)
      ) {
        return infoNotification(t('class.class.message22'));
      }

      // // 이전/이후 수업일 이내인지 확인
      const prevIndex = eventIndex > 0 ? eventIndex - 1 : null;
      const nextIndex =
        eventIndex === timetableData.length - 1 ? null : eventIndex + 1;
      let prevDate = null;
      let nextDate = null;

      if (prevIndex !== null) prevDate = timetableData[prevIndex]?.srtDate;
      if (nextIndex !== null) nextDate = timetableData[nextIndex]?.srtDate;

      if (prevDate) {
        if (value === prevDate || moment(value).isBefore(prevDate)) {
          return infoNotification(t('class.class.message23'));
        }
      }

      if (nextDate) {
        if (value === nextDate || moment(value).isAfter(nextDate)) {
          return infoNotification(t('class.class.message24'));
        }
      }
    }

    const _inputs: any = { ...inputs };
    _inputs[key] = value;
    setInputs(_inputs);
  };

  // teacher 입력 함수
  const onSetTeacher = (teacher: IStaff) => {
    const newInputs = { ...inputs };
    newInputs.classTime[teacherInputRef].teacher = teacher;
    setInputs(newInputs);
  };

  // 시작시간 입력 함수
  const onSetStartTime = (index: number, value: string) => {
    const newInputs: any = { ...inputs };
    newInputs.classTime[index].srtTime = value;
    setInputs(newInputs);
  };

  const onSetClassRoom = (index: number, value: string) => {
    const newInputs: any = { ...inputs };
    newInputs.classTime[index].classRoomCode = value;
    setInputs(newInputs);
  };

  const onSubmit = async () => {
    if (!inputs.changeDate) {
      return infoNotification(t('class.class.message25'));
    }

    for (let i = 0; i < inputs.classTime.length; i++) {
      if (!inputs.classTime[i].srtTime) {
        return infoNotification(t('class.class.message26'));
      }
    }

    const uniqueTimes = _.uniqBy(inputs.classTime, 'srtTime');
    if (inputs.classTime.length > uniqueTimes.length) {
      return infoNotification(t('class.class.message27'));
    }

    const req = {
      changeDate: inputs.changeDate,
      classTime: inputs.classTime.map((item: any) => {
        return {
          classTimeSeq: item.classTimeSeq,
          srtTime: item.srtTime,
          classRoomCode: item.classRoomCode,
          teacherCode: item.teacher.memberCode,
        };
      }),
    };

    await updateExecute(updateTimetable(timetable.data.timetableCode, req));
  };

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={classroomError} />
      <ErrorResponse error={updateError} />

      <div className="comm-bx__between"> 
        <h3 className="sub-title">{t('class.class.title10')}</h3>
      </div>
      <div className='comm-table__area rowType edit'>
        <table>
          <colgroup>
            <col width="30%" />
            <col width="*" />
          </colgroup>
          <tbody>
            <tr>
              <th className="text-center" style={{ verticalAlign: 'middle' }}>
                {t('class.class.column65')}
              </th>
              <td>
                {data?.semesterName}
                <br />
                {moment(data?.semesterSrtDate).format('YYYY.MM.DD')} ~{' '}
                {moment(data?.semesterEndDate).format('YYYY.MM.DD')}
              </td>
            </tr>
            {
              data?.semesterType === SEMESTER_TYPE.REGULAR && (
                <tr>
                  <th className="text-center" style={{ verticalAlign: 'middle' }}>
                    {t('class.class.column66')}
                  </th>
                  <td>
                    {t('class.class.text27')}
                    {data?.monthSeq}
                    {t('class.class.text28')}
                    <br />
                    {moment(data?.monthSrtDate).format('YYYY.MM.DD')} ~{' '}
                    {moment(data?.monthEndDate).format('YYYY.MM.DD')}
                  </td>
                </tr>
              )
            }
            <tr>
              <th className="text-center align-middle">
                {t('class.class.column67')}
              </th>
              <td>
                <div className="date-picker__area">
                  <DatePicker
                    value={
                      inputs?.changeDate ? moment(inputs.changeDate) : undefined
                    }
                    onChange={(e) => {
                      if (e === null) onSetInputs('changeDate', null);
                      else onSetInputs('changeDate', moment(e).format('YYYYMMDD'));
                    }}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="comm-bx__between"> 
        <h3 className='sub-title'>{t('class.class.title11')}</h3>
      </div>
      <div className='comm-table__area'>
        <table>
          <colgroup>
            <col width="10%" />
            <col width="17%" />
            <col width="20%" />
            <col width="20%" />
            <col width="13%" />
            <col width="20%" />
          </colgroup>
          <thead>
            <tr>
              <th className="text-center">{t('class.class.column68')}</th>
              <th className="text-center">{t('class.class.column69')}</th>
              <th className="text-center">{t('class.class.column70')}</th>
              <th className="text-center">{t('class.class.column71')}</th>
              <th className="text-center">{t('class.class.column72')}</th>
              <th className="text-center">{t('class.class.column73')}</th>
            </tr>
          </thead>
          <tbody>
            {inputs?.classTime?.map((item: any, index: number) => (
              <tr>
                <td className="text-center">{item.classTimeSeq}</td>
                <td className="text-center">
                  <div className='time-picker__area'>
                    <CustomTimePicker
                      value={item.srtTime}
                      onSelect={(value) =>
                        onSetStartTime(index, moment(value).format('HH:mm'))
                      }
                      minuteStep={10}
                    />
                  </div>
                </td>
                <td className="text-center">
                  <Select
                    className="w-100"
                    placeholder={t('common.button.select')}
                    value={String(item.classRoomCode)}
                    onChange={(value) => onSetClassRoom(index, value)}
                  >
                    {classroomList?.map((subitem: any) => {
                      return (
                        <Option key={subitem.value} value={subitem.value}>
                          {subitem.label}
                        </Option>
                      );
                    })}
                  </Select>
                </td>
                <td>
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small black mr-1"
                      onClick={() => {
                        setTeacherInputRef(index);
                        setSelectTeacherVisible(true);
                      }}
                      text={t('common.button.search')}
                    />
                    <span className='v-line'>{item.teacher?.memberName}</span>
                  </div>
                </td>
                <td className="text-center">{item.subjectName}</td>
                <td className="text-center">{item.lessonName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('schedule')}
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {/* 강사 배정 모달 */}
      <SelectTeacherModal
        visible={selectTeacherVisible}
        setVisible={setSelectTeacherVisible}
        campusCode={chosenClass?.campusCode}
        setTeacher={onSetTeacher}
      />
    </div>
  );
};

export default ClassCalendarDetail;
